import { ref } from 'vue'
import { is, clone } from 'ramda'
import { useCore } from '../core/index.js'
import { makeCustomEvents } from './custom-events/index.js'
import {
  NAME,
  ERROR_TIMEOUT,
  ERROR_UNKNOWN,
} from './consts.js'

import PERSISTENT_LISTENERS from './persistent-listeners/index.js'

import { logger } from './log.js'

// "persistent" listeners
const LISTENERS = {
  // '[event name]': [
  //   {
  //     callback: (data) => {},
  //     once: false
  //   },
  //   ...
  // ],
  // ...
}

// event callbacks
const CALLBACKS = {
  // '[event name]': {
  //   resolve: (data) => {},
  //   rejectTimeout: () => {}
  // },
  // ...
}

let ready = ref(false)

function checkIsInitialized () {
  if (!ready.value) {
    throw new Error(`${NAME} not initialized`)
  }
}

/*
 * sends simple event to native application
 */
function sendEvent (name, data = {}, { target, origin } = {}) {
  checkIsInitialized()

  const { store } = useCore()

  if (!name) {
    throw new Error('web-view event name not given')
  }

  const message = JSON.stringify({ name, data })
  /*
   * force target
   *
   * xxx: remove this functionality
   * (this service is ONLY for communication with native apps.)
   */
  if (target) {
    try {
      target.postMessage(message, origin)
      logger.debug(`message sent to given target: ${message}`)
    } catch (error) {
      logger.error(error)
      throw error
    }
    // shortcut
    return
  }

  /*
   * send event to iOS native app.
   */
  if (store.getters['app/isIOS']) {
    try {
      if (window.webkit) {
        window.webkit.messageHandlers.BooksyWebViewInterface.postMessage(message)
        logger.debug(`message sent to iOS native app.: ${message}`)
      } else {
        logger.debug(`window.webkit not available. web debug: Message iOS native app.: ${message}`)
      }
    } catch (error) {
      logger.error(error)
      throw error
    }
    // shortcut
    return
  }

  /*
   * send event to Android native app.
   */
  if (store.getters['app/isAndroid']) {
    try {
      window.BooksyWebViewInterface.postMessage(message)
      logger.debug(`message sent to Android native app.: ${message}`)
    } catch (error) {
      logger.error(error)
      throw error
    }
    // shortcut
    return
  }

  // debug (development mode only)
  if (process.env.NODE_ENV === 'development') {
    logger.debug(`message sent: ${message}`)
    // shortcut
    return
  }

  const error = `no message target detected for message: ${message}`
  logger.error(error)
  throw new Error(error)
}

/*
 * send event with callback
 */
function sendEventWithCallback (name, data = {}, timeout = 20000) {
  checkIsInitialized()

  return new Promise((resolve, reject) => {
    // register callback
    CALLBACKS[name] = {
      resolve: (data) => {
        clearTimeout(CALLBACKS[name].timeoutId)
        delete CALLBACKS[name]
        resolve(data)
      },
      timeoutId: setTimeout(() => {
        delete CALLBACKS[name]
        logger.error(`event "${name}" timed out`)
        reject(new Error(`${ERROR_TIMEOUT}: event "${name}"`))
      }, timeout),
    }

    try {
      sendEvent(name, data)
    } catch (error) {
      clearTimeout(CALLBACKS[name].timeoutId)
      delete CALLBACKS[name]
      reject(error)
    }
  })
}

/*
 * add event listener
 */
function addEventListener (name, callback, options = { once: false }) {
  if (!name) {
    throw new Error('Event name not given')
  }

  if (!callback) {
    throw new Error('Event callback not given')
  }

  if (typeof callback !== 'function') {
    throw new Error('Event callback must be a function')
  }

  const listener = {
    ...options,
    callback,
  }

  if (Array.isArray(LISTENERS[name])) {
    LISTENERS[name].push(listener)
  } else {
    LISTENERS[name] = [listener]
  }
}

/*
 * remove event listener
 */
function removeEventListener (name, callback) {
  if (!name) {
    throw new Error('Event name not given')
  }

  if (!callback) {
    throw new Error('Event callback not given')
  }

  if (typeof callback !== 'function') {
    throw new Error('Event callback must be a function')
  }

  // shortcut
  if (!LISTENERS[name]) return

  // shortcut
  if (!Array.isArray(LISTENERS[name])) return

  // remove listener from list
  LISTENERS[name] = LISTENERS[name].filter(listener => listener.callback !== callback)

  // delete list if empty
  if (!LISTENERS[name].length) {
    delete LISTENERS[name]
  }
}

/*
 * handle message event
 */
function handleMessageEvent (event) {
  const eventDetail = clone(event.detail || event.data)

  try {
    const detail = (
      is(Object, eventDetail)
        ? eventDetail
        : (
          is(String, eventDetail)
            ? JSON.parse(eventDetail)
            : null
        )
    ) || {}

    if (!is(Object, detail) || !is(String, detail.name) || !is(Object, detail.data)) {
      // shortcut: ignore
      return
    }

    const { name, data } = detail

    if (!LISTENERS[name] && !CALLBACKS[name]) {
      logger.debug(`received unexpected event "${name}", skipping...`)
      // shortcut
      return
    }

    logger.debug(`received "${name}" event with data:`, data)

    if (LISTENERS[name]) {
      for (const listener of LISTENERS[name]) {
        try {
          listener.callback(data)
          if (listener.once) {
            removeEventListener(name, listener.callback)
          }
        } catch (error) {
          logger.error(error)
        }
      }
    }

    if (CALLBACKS[name] && CALLBACKS[name].resolve) {
      CALLBACKS[name].resolve(data)
    }
  } catch (error) {
    logger.error(error, eventDetail)
  }
}

/*
 * adds persistent event listeners
 */
function addPersistentListeners () {
  const registered = []

  // register persistent listeners
  PERSISTENT_LISTENERS.forEach(({ name, callback }) => {
    // shortcut: bad name
    if (!is(String, name)) {
      logger.error('persistent listener name not set or not a string')
      return
    }

    // shortcut: bad callback
    if (typeof callback !== 'function') {
      logger.error('persistent listener callback not set or not a function')
      return
    }

    addEventListener(name, callback)
    registered.push(name)
  })

  logger.debug('registered persistent listeners:', registered)
}

/*
 * removes persistent listeners
 */
function removePersistentListeners () {
  const removed = []

  // register persistent listeners
  PERSISTENT_LISTENERS.forEach(({ name, callback }) => {
    if (is(String, name) && (typeof callback === 'function')) {
      removeEventListener(name, callback)
      removed.push(name)
    }
  })

  logger.debug('removed persistent listeners:', removed)
}

/*
 * init
 */
export function init () {
  document.addEventListener('BooksyWebViewMessage', handleMessageEvent)

  addPersistentListeners()

  // xxx: remove this functionality
  // (this service is ONLY for communication with native apps.)
  window.addEventListener('message', handleMessageEvent)

  ready.value = true
}

/*
 * destroy
 */
export function destroy () {
  document.removeEventListener('BooksyWebViewMessage', handleMessageEvent)

  // xxx: remove this functionality
  // (this service is ONLY for communication with native apps.)
  window.removeEventListener('message', handleMessageEvent)

  removePersistentListeners()

  ready.value = false
}

export function useWebViewEventBus () {
  return {
    /* custom events, eg.:
     *   sendEhlo
     *   sendClearWebViewCache
     *   ...
     */
    events: makeCustomEvents({
      sendEvent,
      sendEventWithCallback,
      ERROR_TIMEOUT,
      ERROR_UNKNOWN,
    }),

    ready,

    init,
    destroy,

    sendEvent,
    sendEventWithCallback,
    addEventListener,
    removeEventListener,
  }
}
